import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface JournalEntryLine {
  account_id: string;
  account_number: string;
  account_name: string;
  description: string;
  debit: number;
  credit: number;
  sort_order: number;
}

export interface JournalEntryRow {
  id: string;
  number: string;
  header_date: string;
  description: string;
  company_name: string;
  location_name: string | null;
  audit_sequence: number;
  posted: boolean;
  recurring: boolean;
  total_debits: number;
  total_credits: number;
  lines: JournalEntryLine[];
}

@Component({
  selector: 'app-journal-entry-report',
  templateUrl: './journal-entry-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class JournalEntryReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Journal Entry Report';
  isLoading = false;
  isLoaded = false;
  noDataFound = false;
  rows: JournalEntryRow[] = [];
  accounts: any[] = [];
  companies: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    fromDate: '',
    toDate: '',
    account_id: '',
    entry_type: 'all',
  };

  entryTypeOptions = [
    {value: 'all', label: '— All —'},
    {value: 'journal', label: 'Journal (non-recurring)'},
    {value: 'recurring', label: 'Recurring'},
  ];

  vm: any = {Preferences: [], account: [], accountFilter: {search_string: '', type_name: ''}, startAccount: 0};
  selectedAccountName = '';

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  private getDefaultDates(): { fromDate: any; toDate: any } {
    const today = new Date();
    const from = new Date(today);
    from.setMonth(from.getMonth() - 3);
    return {
      fromDate: {year: from.getFullYear(), month: from.getMonth() + 1, day: from.getDate()},
      toDate: {year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate()},
    };
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';
    const dates = this.getDefaultDates();
    this.filters.fromDate = dates.fromDate;
    this.filters.toDate = dates.toDate;

    this.tableDataService.getData('company?state=active&pageNumber=0&pageSize=999999').subscribe((result: any) => {
      this.companies = result.Data ?? result.items ?? [];
    });

    this.tableDataService.getData('account', '?state=active&pageNumber=0&pageSize=999999').subscribe((result: any) => {
      this.accounts = result.Data ?? result.items ?? [];
      this.vm.account = this.accounts;
      this.isLoaded = true;
    });
  }

  onAccountSelected(accountId: string) {
    this.filters.account_id = accountId;
    const acct = this.accounts.find(a => a.id === accountId);
    this.selectedAccountName = acct ? `${acct.number} ${acct.name}` : '';
    this.vm.accountFilter = {search_string: '', type_name: ''};
  }

  onUpdateAccount() {
    this.tableDataService.getData('account', '?pageNumber=0&pageSize=999999').subscribe((result: any) => {
      this.accounts = result.Data ?? result.items ?? [];
      this.vm.account = this.accounts;
    });
  }

  clearAccountFilter() {
    this.filters.account_id = '';
    this.selectedAccountName = '';
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      fromDate: this.getDefaultDates().fromDate,
      toDate: this.getDefaultDates().toDate,
      account_id: '',
      entry_type: 'all',
    };
    this.selectedAccountName = '';
    this.vm.accountFilter = {search_string: '', type_name: ''};
    this.rows = [];
    this.vm.report = null;
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    if (!this.filters.company_id) {
      this.notification.showWarningToast('Please select a Company before running the report.');
      return;
    }
    if (!this.filters.fromDate) {
      this.notification.showWarningToast('Please select a Start Date before running the report.');
      return;
    }
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];

    let params = 'company_id=' + (this.filters.company_id || '');

    const fd = this.dateFilter.GetDateFormat(this.filters.fromDate);
    const td = this.dateFilter.GetDateFormat(this.filters.toDate);
    if (fd) {
      params += '&fromDate=' + fd;
    }
    if (td) {
      params += '&toDate=' + td;
    }
    if (this.filters.account_id) {
      params += '&account_id=' + this.filters.account_id;
    }
    if (this.filters.entry_type && this.filters.entry_type !== 'all') {
      params += '&entry_type=' + this.filters.entry_type;
    }

    this.tableDataService.getDataWithParams('JournalEntryReport', params).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        this.noDataFound = this.rows.length === 0;
        this.vm.report = this.rows;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('Journal Entry Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `JournalEntryReport_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
